/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateStore } from "../../api/mutations";
import { useLanguage } from "../../contexts/LanguageContext";
import StoreTimeZoneField from "../../components/StoreTimeZoneField";
import { currencyOptionsFor } from "../../constants/currencies";
import { getApiErrorMessages } from "../../utils/apiErrorReasons";
import { ErrorMessages } from "../../components/ui/Modal";
import {
  buttonClass,
  cardClass,
  inputClass,
  labelClass,
} from "../../components/ui/styles";
import { baseStorePayload } from "./storePayload";

function validateName(name, t) {
  const trimmed = name.trim();
  if (!trimmed) return t("settings.storeName.required");
  if (trimmed.length < 2) return t("settings.storeName.minLength");
  if (trimmed.length > 50) return t("settings.storeName.maxLength");
  return "";
}

export default function GeneralSection({ store }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [name, setName] = useState(store.name || "");
  const [currency, setCurrency] = useState(store.currency || "€");
  const [timeZone, setTimeZone] = useState(store.time_zone || "");
  const [nameTouched, setNameTouched] = useState(false);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: updateStore,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["stores"] });
      setSaved(true);
    },
  });

  const nameError = validateName(name, t);
  const edited = (setter) => (value) => {
    setSaved(false);
    setter(value);
  };

  const submit = (e) => {
    e.preventDefault();
    setNameTouched(true);
    if (nameError) return;
    setSaved(false);
    mutation.mutate({
      ...baseStorePayload(store),
      name: name.trim(),
      currency,
      timeZone,
    });
  };

  const showNameError = nameTouched && nameError;

  return (
    <form
      onSubmit={submit}
      noValidate
      className={`${cardClass} flex flex-col gap-[18px] p-6`}
    >
      <h2 className="text-[17px] font-semibold text-gray-900">
        {t("settings.sections.general")}
      </h2>

      <div className="flex max-w-[420px] flex-col gap-1.5">
        <label className={labelClass}>
          {t("settings.storeName.label")}
          <input
            type="text"
            name="store-name"
            value={name}
            onChange={(e) => edited(setName)(e.target.value)}
            onBlur={() => setNameTouched(true)}
            aria-invalid={showNameError ? true : undefined}
            aria-describedby={showNameError ? "store-name-error" : undefined}
            className={`${inputClass} font-normal ${showNameError ? "border-red-400" : ""}`}
          />
        </label>
        {showNameError && (
          <span id="store-name-error" className="text-[13px] text-red-700">
            {nameError}
          </span>
        )}
      </div>

      <div className="grid max-w-[640px] grid-cols-1 gap-4 sm:grid-cols-2">
        <label className={labelClass}>
          {t("store.currency")}
          <select
            id="currency"
            name="currency"
            value={currency}
            onChange={(e) => edited(setCurrency)(e.target.value)}
            className={`${inputClass} font-normal`}
          >
            {currencyOptionsFor(currency).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <StoreTimeZoneField
          value={timeZone}
          onChange={edited(setTimeZone)}
          savedTimeZone={store.time_zone || null}
          t={t}
        />
      </div>
      <p className="text-[13px] text-gray-600">
        {t("settings.general.timeZoneHint")}
      </p>

      {mutation.isError && (
        <ErrorMessages
          messages={getApiErrorMessages(
            mutation.error,
            t,
            "errors.updateStoreFailed",
          )}
        />
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={mutation.isPending}
          className={buttonClass.primary}
        >
          {mutation.isPending ? t("common.saving") : t("common.save")}
        </button>
        <span role="status" className="text-sm text-emerald-800">
          {saved ? t("common.savedChanges") : ""}
        </span>
      </div>
    </form>
  );
}
